import { useEffect, useRef, useState } from 'react';
import { Dialog } from './dialog';
import { Button } from 'primereact/button';
import { Rating } from 'primereact/rating';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { CourseFeedbackPromptDto } from '../../general/dtos';
import { useApi } from '../../providers/api-provider';
import { useFullscreenDialogParent } from '../../general/use-fullscreen-dialog-parent';

export function CourseFeedbackDialog({ course, courses, sessionId, onDone, onSubmitted, parent, embedded = false, onBusyChange }: {
    embedded?: boolean; onBusyChange?: (busy: boolean) => void;
    course?: CourseFeedbackPromptDto; courses?: CourseFeedbackPromptDto[]; sessionId?: string; onDone: () => void;
    onSubmitted?: () => void;
    parent?: HTMLElement | null;
}) {
    const api = useApi();
    const dialogParent = useFullscreenDialogParent(parent);
    const options = course ? [course] : courses ?? [];
    const [courseKey, setCourseKey] = useState<string | null>(course?.course_key ?? (options.length === 1 ? options[0].course_key : null));
    const selectedCourse = options.find(option => option.course_key === courseKey);
    const [stars, setStars] = useState<number | null>(null);
    const [text, setText] = useState('');
    const [share, setShare] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const saving = useRef(false);
    const active = useRef(true);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
    const hasText = text.trim().length > 0;
    const submit = async () => {
        if (!api || saving.current || !stars || !selectedCourse) return;
        saving.current = true; setBusy(true); onBusyChange?.(true); setError('');
        try {
            await api.submitCourseFeedback(sessionId, { course_key: selectedCourse.course_key, stars, text: text.trim(), share_with_others: share && hasText });
            if (active.current) { onSubmitted?.(); onDone(); }
        } catch (failure: any) {
            if (active.current) setError(failure?.response?.data?.message || failure?.message || 'Could not save your feedback. Please try again.');
        } finally { saving.current = false; if (active.current) { setBusy(false); onBusyChange?.(false); } }
    };
    const content = <>
        <p>{selectedCourse ? <>You’ve completed <strong style={{ textDecoration: 'underline', textUnderlineOffset: '0.15em' }}>{selectedCourse.course_name}</strong>. How was the product?</>
            : 'Choose a completed product to leave your feedback.'}</p>
        {selectedCourse?.certificate_enabled === false && <p>This course does not include a certificate.</p>}
        <div className="flex flex-column gap-3">
            {!course && options.length > 1 && <>
                <label htmlFor="product-feedback-product">Product to review</label>
                <Dropdown inputId="product-feedback-product" value={courseKey} options={options} optionLabel="course_name" optionValue="course_key"
                    placeholder="Select a completed product" disabled={busy} appendTo={dialogParent} className="w-full"
                    onChange={event => { setCourseKey(event.value); setStars(null); setText(''); setShare(false); setError(''); }} />
            </>}
            <label id="course-feedback-rating">Your rating (required)</label>
            <Rating value={stars ?? undefined} stars={5} cancel={false} disabled={busy} aria-labelledby="course-feedback-rating"
                onChange={event => setStars(event.value ?? null)} />
            <label htmlFor="course-feedback-text">Your feedback (optional)</label>
            <InputTextarea id="course-feedback-text" value={text} rows={5} maxLength={2000} autoResize disabled={busy}
                className="w-full" onChange={event => {
                    setText(event.target.value);
                    if (!event.target.value.trim()) setShare(false);
                }} />
            <small className="text-color-secondary">Up to 2,000 characters.</small>
            <div className="flex align-items-start gap-2">
                <Checkbox inputId="course-feedback-share" checked={share && hasText} disabled={busy || !hasText}
                    onChange={event => setShare(!!event.checked)} />
                <label htmlFor="course-feedback-share">Share my feedback with other users</label>
            </div>
            <small className="text-color-secondary">Add written feedback to enable sharing.</small>
            {error && <p role="alert">{error}</p>}
            <div className="flex justify-content-end gap-2">
                {!embedded && <Button label="Skip" text disabled={busy} onClick={onDone} />}
                <Button label="Submit feedback" disabled={busy || !stars || !api || !selectedCourse} loading={busy} onClick={() => void submit()} />
            </div>
        </div>
    </>;
    if (embedded) return content;
    return <Dialog header="Product feedback" visible modal
        appendTo={dialogParent} baseZIndex={11000}
        style={{ width: '36rem', maxWidth: '95vw' }} draggable={false}
        closable={!busy} closeOnEscape={!busy} onHide={() => { if (!busy) onDone(); }}>
        {content}
    </Dialog>;
}
